import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, QrCode, MapPin, CalendarDays, Trophy, TrendingUp, Flame, Crown, Swords, Users, Building2, AlertTriangle, RotateCw, ArrowUpRight, Settings, ListOrdered } from "lucide-react";
import { api, qrUrl, SITE_ORIGIN } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, SimBadge } from "../components/Badges";
import { RankChange } from "../components/Rating";
import { ClubCrest } from "../components/ClubCrest";
import { Flag } from "../components/Flag";
import { CinematicPortrait } from "../components/CinematicPortrait";
import { RecordsHeld } from "../components/RecordsHeld";
import { StandingsShareButton, FixtureShareButton, SeasonShareButton, MatchdayShareBar, LeadersShareButton, WeeklyPackButton, fmtKickoff } from "../components/StandingsShare";

const CONTAINER = "mx-auto max-w-[1500px] px-4 sm:px-8 lg:px-12";
const PANEL = "rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.18)]";
const CLUB_RANK_WORLD = "/rankings?entity=clubs&ranking=overall&scope=world";
const CLUB_RANK_COUNTRY = "/rankings?entity=clubs&ranking=overall&scope=country";

function PanelHead({ Icon, children, testid }) {
  return (
    <div data-testid={testid} className="px-5 py-3 border-b border-[#2b2512] font-heading font-bold uppercase tracking-wide text-slate-200 text-sm flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4 text-[#E5B842]" />} {children}
    </div>
  );
}

export default function ClubPassportPage() {
  const { clubId } = useParams();
  const { user } = useAuth();
  const [club, setClub] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  const load = useCallback(() => {
    setClub(null); setNotFound(false); setError(false);
    api.get(`/clubs/${clubId}`)
      .then(({ data }) => setClub(data))
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); else setError(true); });
  }, [clubId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!user || !club) { setIsOwner(false); return; }
    api.get("/clubs/mine/owned")
      .then(({ data }) => setIsOwner((data.clubs || []).some((c) => c.id === club.id)))
      .catch(() => setIsOwner(false));
  }, [user, club]);

  if (notFound) return <div className={`${CONTAINER} py-16 animate-fade-up`}><div data-testid="club-not-found" className="text-center py-20 text-slate-500 font-mono text-sm uppercase tracking-widest border border-dashed border-[rgba(212,175,55,0.25)] rounded-xl">Club not found</div></div>;
  if (error) return (
    <div className={`${CONTAINER} py-16 animate-fade-up`}>
      <div data-testid="club-error" className="text-center py-20 border border-dashed border-rose-500/30 rounded-xl">
        <AlertTriangle className="w-7 h-7 text-rose-400 mx-auto mb-3" />
        <div className="text-slate-300 font-mono text-sm uppercase tracking-widest mb-4">Could not load this club</div>
        <button onClick={load} data-testid="club-retry" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#161616] border border-[rgba(212,175,55,0.3)] text-slate-200 hover:border-[#E5B842]/60 hover:text-[#E5B842]" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }}><RotateCw className="w-4 h-4" /> Retry</button>
      </div>
    </div>
  );
  if (!club) return (
    <div className={`${CONTAINER} py-16`} data-testid="club-loading">
      <div className="h-5 w-32 bg-[#141414] rounded mb-6 animate-pulse" />
      <div className="h-48 rounded-2xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.14)] animate-pulse" />
      <div className="grid lg:grid-cols-3 gap-4 mt-4">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-40 rounded-2xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.12)] animate-pulse" />)}</div>
    </div>
  );

  const verifyLink = `${SITE_ORIGIN}/verify/club/${club.wsbf_club_id}`;
  const r = club.rating;
  const intl = club.international;

  return (
    <div className="animate-fade-up pb-16">
      <div className={`${CONTAINER} pt-8`}>
        <Link to="/clubs" data-testid="club-back" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#E5B842] mb-6" style={{ transition: "color 0.15s ease" }}>
          <ArrowLeft className="w-4 h-4" /> Back to clubs
        </Link>
        {isOwner && (
          <Link to="/club-builder" data-testid="club-manage" className="inline-flex items-center gap-1.5 text-sm text-[#E5B842] hover:text-[#F0C72A] mb-6 ml-4 font-semibold" style={{ transition: "color 0.15s ease" }}>
            <Settings className="w-4 h-4" /> Manage club
          </Link>
        )}

        {/* ROW 1 — full-width club credential */}
        <div data-testid="club-passport-card" className="relative overflow-hidden rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707]">
          <div className="absolute inset-0 mesh-grid opacity-[0.22] pointer-events-none" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(90% 120% at 90% 0%, rgba(212,175,55,0.12) 0%, transparent 55%)" }} />
          <div className="relative p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1.1fr)_auto] gap-8 lg:gap-6 lg:items-center">
            {/* identity */}
            <div className="flex items-start gap-5 lg:border-r lg:border-[#2b2512] lg:pr-6">
              <ClubCrest club={club} size={88} />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-50 leading-none wsbf-slant-block break-words">{club.name}</h1>
                  {club.is_simulated && <SimBadge />}
                  <StatusBadge status={club.status} />
                </div>
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-400">
                  <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /><Flag country={club.country} width={18} /> {club.city ? `${club.city}, ${club.country?.name}` : club.country?.name}</span>
                  {club.founded_year && <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" />Est. {club.founded_year}</span>}
                </div>
                <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2.5">
                  <div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-0.5">WSTF Club ID</div><span className="font-mono font-bold tracking-widest text-[#E5B842]">{club.wsbf_club_id}</span></div>
                  <div><div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-0.5">Federation</div>
                    {club.federation ? <Link to={`/federation/${club.federation.wsbf_fed_id}`} data-testid="club-federation-link" className="text-slate-100 hover:text-[#E5B842] text-sm" style={{ transition: "color 0.15s ease" }}>{club.federation.name}</Link> : <span className="text-slate-500 italic text-sm">—</span>}
                  </div>
                  <div className="col-span-2">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono mb-1">Roster</div>
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-bold text-slate-100">{club.roster.count}<span className="text-slate-600">/{club.roster.max}</span></span>
                      <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${club.roster.eligible ? "bg-emerald-950/50 text-emerald-300 border border-emerald-500/30" : "bg-amber-950/50 text-amber-300 border border-amber-500/30"}`}>{club.roster.label}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* performance */}
            <div className="grid grid-cols-3 gap-3 lg:pr-6 lg:border-r lg:border-[#2b2512]">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">Rating</div>
                {r?.has_rating ? <div data-testid="club-rating" className="font-heading text-4xl font-black text-[#E5B842] tabular-nums leading-none">{Math.round(r.rating)}<span className="text-[10px] font-mono text-slate-500 ml-1 align-top">±{Math.round(r.rd)}</span></div> : <div className="font-heading text-lg font-bold text-slate-500">Unrated</div>}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">World Rank</div>
                {r?.world_rank ? <Link to={CLUB_RANK_WORLD} data-testid="club-world-rank" className="font-heading text-4xl font-black text-slate-50 tabular-nums leading-none flex items-baseline gap-1 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>#{r.world_rank.position}<RankChange change={r.world_rank.change} /></Link> : <div className="font-heading text-lg font-bold text-slate-600">—</div>}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">{club.country?.name || "National"} Rank</div>
                {r?.national_rank ? <Link to={CLUB_RANK_COUNTRY} data-testid="club-national-rank" className="font-heading text-4xl font-black text-slate-50 tabular-nums leading-none flex items-baseline gap-1 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>#{r.national_rank.position}<RankChange change={r.national_rank.change} /></Link> : <div className="font-heading text-lg font-bold text-slate-600">—</div>}
              </div>
              {club.season && (
                <div className="col-span-3 mt-1 pt-3 border-t border-[#2b2512] text-[11px] font-mono text-slate-400">
                  Season {club.season.won}-{club.season.drawn}-{club.season.lost} · {club.season.league_points} pts · TPD {club.season.tpf - club.season.tpa}
                </div>
              )}
            </div>
            {/* QR */}
            <div className="flex lg:flex-col items-center lg:items-end gap-4 lg:text-right">
              <img data-testid="club-qr" src={qrUrl(verifyLink)} alt="club verification QR" className="w-24 h-24 rounded-lg bg-white p-1.5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-1 flex items-center gap-1 lg:justify-end"><QrCode className="w-3 h-3" /> Verify club</div>
                <Link to={`/verify/club/${club.wsbf_club_id}`} className="text-xs text-slate-400 hover:text-[#E5B842] font-mono break-all" style={{ transition: "color 0.15s ease" }}>/verify/club/{club.wsbf_club_id}</Link>
              </div>
            </div>
          </div>
          <div className="relative mx-6 sm:mx-8 mb-6 h-1.5 rounded-full" style={{ background: "linear-gradient(90deg,#9B781E,#F3D279,#9B781E)" }} />
        </div>

        {/* ROW 2 — Rankings | Roster Status | Management */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6 items-start">
          <div className={`${PANEL} p-5`} data-testid="club-rankings-panel">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E5B842] font-mono mb-3 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Club Rankings</div>
            <div className="grid grid-cols-2 gap-4">
              <Link to={CLUB_RANK_WORLD} className="group rounded-lg bg-[#161616]/60 border border-[#2b2512] p-3 hover:border-[#E5B842]/40" style={{ transition: "border-color 0.15s ease" }}>
                <div className="text-[10px] uppercase text-slate-500 font-mono flex items-center justify-between">World <ArrowUpRight className="w-3 h-3 text-slate-600 group-hover:text-[#E5B842]" /></div>
                <div className="font-heading text-2xl font-black text-slate-50">#{r?.world_rank?.position ?? "—"}</div>
              </Link>
              <Link to={CLUB_RANK_COUNTRY} className="group rounded-lg bg-[#161616]/60 border border-[#2b2512] p-3 hover:border-[#E5B842]/40" style={{ transition: "border-color 0.15s ease" }}>
                <div className="text-[10px] uppercase text-slate-500 font-mono flex items-center justify-between">{club.country?.name || "Country"} <ArrowUpRight className="w-3 h-3 text-slate-600 group-hover:text-[#E5B842]" /></div>
                <div className="font-heading text-2xl font-black text-slate-50">#{r?.national_rank?.position ?? "—"}</div>
              </Link>
            </div>
            {r?.has_rating && <div className="mt-3 text-[11px] font-mono text-slate-500">Record {r.wins}-{r.draws}-{r.losses} · Peak {Math.round(r.peak_rating)} · {r.matches_count} matches</div>}
          </div>

          <div className={`${PANEL} p-5`} data-testid="club-roster-status">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E5B842] font-mono mb-3 flex items-center gap-1"><Users className="w-3 h-3" /> Roster Status</div>
            <div className="flex items-end gap-2"><span className="font-heading text-4xl font-black text-slate-50 tabular-nums leading-none">{club.roster.count}</span><span className="text-sm font-mono text-slate-500 mb-1">/ {club.roster.max}</span></div>
            <div className="h-2 rounded-full bg-[#1a1407] overflow-hidden mt-3"><div className="h-full rounded-full" style={{ width: `${Math.min(100, (club.roster.count / club.roster.max) * 100)}%`, background: club.roster.eligible ? "linear-gradient(90deg,#9B781E,#F3D279)" : "#b45309" }} /></div>
            <div className={`mt-2 text-xs font-mono uppercase tracking-wider ${club.roster.eligible ? "text-emerald-400" : "text-amber-400"}`}>{club.roster.label}</div>
            <div className="text-[10px] font-mono text-slate-600 mt-1">Minimum {club.roster.min} to compete</div>
          </div>

          <div className={`${PANEL} p-5`} data-testid="club-management">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E5B842] font-mono mb-3 flex items-center gap-1"><Building2 className="w-3 h-3" /> Management</div>
            <div className="text-[10px] uppercase text-slate-500 font-mono">Owner</div>
            {club.owner ? <Link to={`/player/${club.owner.wsbf_player_id}`} className="text-slate-100 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>{club.owner.display_name}<div className="font-mono text-[11px] text-slate-500">{club.owner.wsbf_player_id}</div></Link> : <div className="text-slate-400 italic text-sm">Federation-managed</div>}
            {club.federation && <><div className="text-[10px] uppercase text-slate-500 font-mono mt-3">Federation</div><Link to={`/federation/${club.federation.wsbf_fed_id}`} className="text-slate-100 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>{club.federation.name}<div className="font-mono text-[11px] text-slate-500">{club.federation.wsbf_fed_id}</div></Link></>}
          </div>
        </div>

        {/* ROW 3 — Active Roster (primary) + side column */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4 items-start">
          <div className={`lg:col-span-2 ${PANEL} overflow-hidden`}>
            <PanelHead Icon={Users}>Active Roster</PanelHead>
            <table className="w-full text-sm" data-testid="club-roster-table">
              <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 font-mono bg-[#070707]">
                <th className="text-left px-5 py-3 w-12">No.</th>
                <th className="text-left px-2 py-3">Player</th>
                <th className="text-left px-2 py-3 hidden sm:table-cell">WSTF ID</th>
                <th className="text-left px-2 py-3 hidden md:table-cell">Role</th>
              </tr></thead>
              <tbody>
                {(club.roster_members || []).map((m) => {
                  const isOwner = m.wsbf_player_id && m.wsbf_player_id === club.owner?.wsbf_player_id;
                  return (
                  <tr key={m.id} className="border-t border-[#2b2512]/60 hover:bg-[#161616]/60" style={{ transition: "background-color 0.15s ease" }}>
                    <td className="px-5 py-3 font-heading font-black text-[#E5B842]">{m.jersey_number ?? "—"}</td>
                    <td className="px-2 py-3"><Link to={`/player/${m.wsbf_player_id}`} className="flex items-center gap-2 text-slate-100 hover:text-[#E5B842] font-semibold" style={{ transition: "color 0.15s ease" }}><CinematicPortrait player={m} variant="thumb" className="w-6 h-8 shrink-0" /><Flag country={m.country} width={16} />{m.display_name}{m.is_simulated && <SimBadge />}{isOwner && <span data-testid={`roster-owner-badge-${m.wsbf_player_id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold uppercase tracking-wider bg-[#E5B842]/15 border border-[#E5B842]/40 text-[#E5B842]"><Crown className="w-2.5 h-2.5" />Owner</span>}</Link></td>
                    <td className="px-2 py-3 font-mono text-[11px] text-slate-400 hidden sm:table-cell">{m.wsbf_player_id}</td>
                    <td className="px-2 py-3 hidden md:table-cell">{m.position ? <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-mono font-bold uppercase tracking-wider bg-[#141414] border border-[#2b2512] text-slate-400">{m.position}</span> : "—"}</td>
                  </tr>);
                })}
              </tbody>
            </table>
          </div>

          <div className="space-y-4">
            {r?.has_rating && (
              <div className={`${PANEL} p-5`} data-testid="club-rating-card">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#E5B842] font-mono mb-3 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Club Rating</div>
                <div className="flex items-end gap-2"><span className="font-heading text-4xl font-black text-[#E5B842] tabular-nums leading-none">{Math.round(r.rating)}</span><span className="text-[11px] font-mono text-slate-500 mb-1">±{Math.round(r.rd)}</span></div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-[10px] uppercase text-slate-500 font-mono">World</div><div className="font-heading font-bold text-slate-100 flex items-center gap-1.5">#{r.world_rank?.position ?? "—"} <RankChange change={r.world_rank?.change} /></div></div>
                  <div><div className="text-[10px] uppercase text-slate-500 font-mono">{club.country?.name}</div><div className="font-heading font-bold text-slate-100 flex items-center gap-1.5">#{r.national_rank?.position ?? "—"} <RankChange change={r.national_rank?.change} /></div></div>
                </div>
              </div>
            )}
            {club.top_contributors?.length > 0 && (
              <div className={`${PANEL} p-5`} data-testid="club-top-contributors">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-3 flex items-center gap-1"><Flame className="w-3 h-3 text-[#E5B842]" /> Top Contributors</div>
                <ul className="space-y-2">
                  {club.top_contributors.map((t, i) => (
                    <li key={i} className="flex items-center justify-between gap-2 text-sm">
                      <Link to={`/player/${t.player.wsbf_player_id}`} className="flex items-center gap-1.5 text-slate-200 hover:text-[#E5B842] truncate" style={{ transition: "color 0.15s ease" }}>{i === 0 && <Trophy className="w-3.5 h-3.5 text-[#E5B842]" />}{t.player.display_name}{t.player.is_simulated && <SimBadge />}</Link>
                      <span className="font-mono text-[#E5B842] tabular-nums shrink-0" title="Team Points generated">{t.team_points_generated} TP</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {club.rivalries?.length > 0 && (
              <div className={`${PANEL} p-2`} data-testid="club-rivalries">
                <div className="px-3 pt-2 pb-1 text-[10px] font-mono uppercase tracking-[0.2em] text-[#E5B842] flex items-center gap-1.5"><Swords className="w-3.5 h-3.5" /> Rivalries</div>
                {club.rivalries.slice(0, 6).map((rv) => (
                  <Link key={rv.opponent.slug} to={rv.rivalry_link} data-testid={`club-rivalry-${rv.opponent.slug}`} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-[#161616]/60 text-sm" style={{ transition: "background-color 0.15s ease" }}>
                    <span className="text-slate-200 truncate">vs {rv.opponent.name}</span>
                    <span className="font-mono text-[10px] uppercase text-slate-500 shrink-0" title="Meetings">{rv.meetings} meetings →</span>
                  </Link>
                ))}
              </div>
            )}
            <RecordsHeld records={club.records_held} testid="club-records-held" />
          </div>
        </div>

        {/* ROW 4 — International History full width */}
        {intl?.has_history && (
          <div className={`${PANEL} overflow-hidden mt-4`} data-testid="club-international">
            <PanelHead Icon={Trophy}>International History</PanelHead>
            <div className="p-5 space-y-5">
              {intl.honours?.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="club-intl-honours">
                  {intl.honours.map((h, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide foil-border bg-gradient-to-r from-[#2a2410] to-[#070707] text-[#E5B842]"><Crown className="w-3.5 h-3.5" /> {h.title}</span>
                  ))}
                </div>
              )}
              {intl.appearances?.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {intl.appearances.map((a) => (
                    <Link key={a.competition_id} to={`/continental/${a.tier}`} className="block rounded-lg bg-[#161616]/60 border border-[#2b2512] p-4 hover:border-[#E5B842]/40" style={{ transition: "border-color 0.15s ease" }}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-heading font-bold uppercase text-slate-100 text-sm truncate">{a.competition_name}</span>
                        {a.seed != null && <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500 shrink-0">Seed #{a.seed}</span>}
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2 text-[11px] font-mono">
                        <span className="text-slate-500">{(a.qualification_method || "").replace(/_/g, " ")}{a.round_reached_name ? ` · ${a.round_reached_name}` : ""}</span>
                        <span className={a.is_champion ? "text-[#E5B842] font-bold" : "text-slate-400"}>{a.result}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
              {intl.recent_results?.length > 0 && (
                <div data-testid="club-intl-results">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono mb-2">Recent Continental Results</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5">
                    {intl.recent_results.map((rr) => (
                      <div key={rr.match_id} className="flex items-center gap-2 text-sm">
                        <span className="font-heading font-bold uppercase text-slate-200 text-xs truncate flex-1 text-right">{rr.home?.short_name}</span>
                        <span className="font-heading text-sm font-black tabular-nums text-[#E5B842] shrink-0">{rr.home_points}–{rr.away_points}</span>
                        <span className="font-heading font-bold uppercase text-slate-200 text-xs truncate flex-1">{rr.away?.short_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {/* ROW 5 — Admin competitions this club is entered in */}
        <ClubCompetitionsSection clubId={club.id} />

      </div>
    </div>
  );
}

function ClubCompetitionsSection({ clubId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get(`/clubs/${clubId}/competitions`)
      .then(({ data }) => { if (!cancel) setData(data); })
      .catch(() => { if (!cancel) setData({ competitions: [] }); });
    return () => { cancel = true; };
  }, [clubId]);
  if (!data) return null;
  const comps = data.competitions || [];
  return (
    <div className={`${PANEL} overflow-hidden mt-4`} data-testid="club-competitions-section">
      <PanelHead Icon={ListOrdered} testid="club-competitions-header">ΔΙΟΡΓΑΝΩΣΕΙΣ / Competitions</PanelHead>
      {comps.length === 0 ? (
        <div className="p-6 text-sm text-slate-500 font-mono" data-testid="club-competitions-empty">
          Αυτή η ομάδα δεν συμμετέχει σε κάποια διοργάνωση ακόμα.
        </div>
      ) : (
        <div className="p-5 space-y-5">
          {comps.map((c) => <CompetitionCard key={c.id} comp={c} viewerClubId={clubId} />)}
        </div>
      )}
    </div>
  );
}

function StandingsTable({ comp, viewerClubId }) {
  const standings = comp.standings;
  if (!standings || standings.length === 0) return null;
  return (
    <div className="border-t border-[#2b2512]" data-testid="club-competition-standings">
      <div className="px-4 pt-3 pb-1 flex items-center justify-between gap-2">
        <span className="text-[10px] font-mono uppercase tracking-wider text-[#E5B842]">ΒΑΘΜΟΛΟΓΙΑ · Standings</span>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          <SeasonShareButton comp={comp} us={standings.find((r) => r.club_id === viewerClubId)} fixtures={comp.fixtures || []} />
          <LeadersShareButton comp={comp} standings={standings} iconOnly />
          <StandingsShareButton comp={comp} viewerClubId={viewerClubId} />
        </div>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 font-mono bg-[#070707]">
          <th className="text-left px-3 py-2 w-8">#</th>
          <th className="text-left px-2 py-2">Club</th>
          <th className="text-center px-1 py-2 w-10">P</th>
          <th className="text-center px-1 py-2 w-10">W</th>
          <th className="text-center px-1 py-2 w-10">D</th>
          <th className="text-center px-1 py-2 w-10">L</th>
          <th className="text-center px-1 py-2 w-14 hidden sm:table-cell">GF:GA</th>
          <th className="text-center px-1 py-2 w-10 hidden sm:table-cell">GD</th>
          <th className="text-center px-1 py-2 w-12 text-[#E5B842]">Pts</th>
        </tr></thead>
        <tbody>
          {standings.map((r) => {
            const isMe = r.club_id === viewerClubId;
            return (
              <tr key={r.club_id} data-testid={`standing-row-${r.club_id}`}
                className={`border-t border-[#2b2512]/60 ${isMe ? "bg-[#2a2410]/60" : ""}`}>
                <td className={`px-3 py-2 font-heading font-black ${isMe ? "text-[#E5B842]" : "text-slate-400"}`}>{r.rank}</td>
                <td className="px-2 py-2">
                  <Link to={`/club/${r.club_id}`} className={`inline-flex items-center gap-2 ${isMe ? "text-[#E5B842]" : "text-slate-200"} hover:text-[#E5B842]`} style={{ transition: "color 0.15s ease" }}>
                    <ClubCrest club={r} size={18} />
                    <span className="font-semibold">{r.short_name || r.name}</span>
                  </Link>
                  <FormDots form={r.form} clubId={r.club_id} />
                </td>
                <td className="px-1 py-2 text-center font-mono tabular-nums text-slate-300">{r.P}</td>
                <td className="px-1 py-2 text-center font-mono tabular-nums text-emerald-300">{r.W}</td>
                <td className="px-1 py-2 text-center font-mono tabular-nums text-slate-300">{r.D}</td>
                <td className="px-1 py-2 text-center font-mono tabular-nums text-rose-300">{r.L}</td>
                <td className="px-1 py-2 text-center font-mono tabular-nums text-slate-400 hidden sm:table-cell">{r.GF}:{r.GA}</td>
                <td className={`px-1 py-2 text-center font-mono tabular-nums hidden sm:table-cell ${r.GD > 0 ? "text-emerald-300" : r.GD < 0 ? "text-rose-300" : "text-slate-400"}`}>{r.GD > 0 ? `+${r.GD}` : r.GD}</td>
                <td className="px-1 py-2 text-center font-heading font-black tabular-nums text-[#E5B842]">{r.Pts}</td>
              </tr>);
          })}
        </tbody>
      </table>
      <div className="px-4 py-2 flex items-center gap-3 text-[10px] font-mono uppercase tracking-wider text-slate-500 border-t border-[#2b2512]/60" data-testid="form-legend">
        <span>Φόρμα · Form (last 5, oldest → newest)</span>
        {Object.entries(FORM_CLS).map(([k, c]) => <span key={k} className="inline-flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${c}`} />{k}</span>)}
      </div>
    </div>
  );
}

const FORM_CLS = { W: "bg-emerald-400", D: "bg-slate-400", L: "bg-rose-400" };
const FORM_LABEL = { W: "Win", D: "Draw", L: "Loss" };

function FormDots({ form, clubId }) {
  const f = form || [];
  const slots = [...Array(Math.max(0, 5 - f.length)).fill(null), ...f];
  return (
    <span className="inline-flex items-center gap-[3px] ml-2 align-middle" data-testid={`standing-form-${clubId}`} data-form={f.join("")}>
      {slots.map((r, i) => (
        <span key={i} title={r ? FORM_LABEL[r] : "No result"}
          className={`w-2 h-2 rounded-full ${r ? FORM_CLS[r] : "bg-[#262626] border border-[#3a3218]"}`} />
      ))}
    </span>
  );
}

function LeagueMatchdayShare({ comp, viewerClubId }) {
  const [all, setAll] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get(`/league-competitions/${comp.id}/fixtures`)
      .then(({ data }) => { if (!cancel) setAll(data.fixtures || []); })
      .catch(() => { if (!cancel) setAll([]); });
    return () => { cancel = true; };
  }, [comp.id]);
  if (!all || all.length === 0) return null;
  return (
    <div className="px-4 py-2 flex items-center justify-between gap-2 border-t border-[#2b2512]" data-testid={`club-matchday-share-${comp.id}`}>
      <span className="text-[10px] font-mono uppercase tracking-wider text-[#E5B842]">ΑΓΩΝΙΣΤΙΚΕΣ · Matchdays</span>
      <div className="inline-flex items-center gap-1.5">
        <MatchdayShareBar comp={comp} fixtures={all} standings={comp.standings} testid={`matchday-share-${comp.id}`} />
        <WeeklyPackButton comp={comp} fixtures={all} standings={comp.standings} viewerClubId={viewerClubId} iconOnly testid={`weekly-pack-${comp.id}`} />
      </div>
    </div>
  );
}

function CompetitionCard({ comp, viewerClubId }) {
  const us = (comp.standings || []).find((r) => r.club_id === viewerClubId) || {};
  const s = comp.stats || {};
  const chip = (txt, cls) => <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase tracking-wider ${cls}`}>{txt}</span>;
  return (
    <div className="rounded-lg bg-[#0a0a0a] border border-[#2b2512] overflow-hidden" data-testid={`club-competition-${comp.id}`}>
      <div className="px-4 py-3 border-b border-[#2b2512] flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-heading font-black uppercase text-slate-50 text-sm truncate">{comp.name}</div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mt-0.5">
            {comp.format} · {comp.season_label} · {comp.status}
          </div>
        </div>
        {s.played > 0 && (
          <div className="flex items-center gap-1.5 text-[10px] font-mono tabular-nums shrink-0">
            {chip(`P ${s.played}`, "bg-[#141414] border border-[#2b2512] text-slate-300")}
            {chip(`W ${s.wins}`, "bg-emerald-950/40 border border-emerald-500/30 text-emerald-300")}
            {chip(`D ${s.draws}`, "bg-slate-800/50 border border-slate-600/30 text-slate-300")}
            {chip(`L ${s.losses}`, "bg-rose-950/40 border border-rose-500/30 text-rose-300")}
            {chip(`${s.goals_for}:${s.goals_against}`, "bg-[#141414] border border-[#2b2512] text-slate-400")}
          </div>
        )}
      </div>
      {(comp.fixtures || []).length === 0 ? (
        <div className="p-4 text-[11px] font-mono text-slate-600">Το πρόγραμμα δεν έχει δημιουργηθεί ακόμα.</div>
      ) : (
        <>
        <StandingsTable comp={comp} viewerClubId={viewerClubId} />
        <LeagueMatchdayShare comp={comp} viewerClubId={viewerClubId} />
        <table className="w-full text-sm" data-testid={`club-competition-fixtures-${comp.id}`}>
          <thead><tr className="text-[10px] uppercase tracking-wider text-slate-500 font-mono bg-[#070707]">
            <th className="text-left px-3 py-2 w-10">MD</th>
            <th className="text-left px-2 py-2">Opponent</th>
            <th className="text-center px-2 py-2 w-10">H/A</th>
            <th className="text-center px-2 py-2 w-20">Score</th>
            <th className="text-center px-2 py-2 w-10">R</th>
            <th className="text-left px-2 py-2 hidden sm:table-cell">Status</th>
            <th className="w-10 px-2 py-2" />
          </tr></thead>
          <tbody>
            {comp.fixtures.map((f) => (
              <tr key={f.id} className="border-t border-[#2b2512]/60" data-testid={`club-competition-fixture-${f.id}`}>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{f.matchday ?? "—"}</td>
                <td className="px-2 py-2">
                  <Link to={`/club/${f.opponent.id}`} className="inline-flex items-center gap-2 text-slate-100 hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>
                    <ClubCrest club={f.opponent} size={20} />
                    <span className="font-semibold">{f.opponent.short_name || f.opponent.name}</span>
                  </Link>
                  {f.status === "SCHEDULED" && (
                    <div className="text-[10px] font-mono text-slate-400 mt-0.5" data-testid={`club-fixture-kickoff-${f.id}`}>{fmtKickoff(f.scheduled_date)}</div>
                  )}
                </td>
                <td className="px-2 py-2 text-center font-mono text-[10px] text-slate-400">{f.venue}</td>
                <td className="px-2 py-2 text-center font-heading font-black tabular-nums text-[#E5B842]">
                  {f.status === "PLAYED" ? `${f.our_score}–${f.their_score}` : "—"}
                </td>
                <td className="px-2 py-2 text-center">
                  {f.result === "W" && <span className="inline-flex w-6 h-6 items-center justify-center rounded text-[10px] font-black bg-emerald-950/50 border border-emerald-500/40 text-emerald-300">W</span>}
                  {f.result === "D" && <span className="inline-flex w-6 h-6 items-center justify-center rounded text-[10px] font-black bg-slate-800/60 border border-slate-500/30 text-slate-300">D</span>}
                  {f.result === "L" && <span className="inline-flex w-6 h-6 items-center justify-center rounded text-[10px] font-black bg-rose-950/50 border border-rose-500/40 text-rose-300">L</span>}
                  {!f.result && <span className="text-slate-700">—</span>}
                </td>
                <td className="px-2 py-2 hidden sm:table-cell font-mono text-[10px] uppercase tracking-wider text-slate-500">{f.status}</td>
                <td className="px-2 py-2 text-right"><FixtureShareButton comp={comp} f={f} us={us} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}
    </div>
  );
}
